import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from "react";
import { Row, Col, FormGroup, Label, Input, Card, CardBody, CardHeader } from "reactstrap";
import ReactDatetime from "components/CustomDatePicker";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { formatDateForPicker } from "../../utils/dateUtils";
import DollarInput from "../DollarInput";
import { toNumber } from "utils/currency";
import EpsInstructionsAccordion from "./EpsInstructionsAccordion";

const PortfolioEpsPpd = forwardRef(({ data, activeColor, isTheme2 = false, portfolioId, updateMutation }, ref) => {
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    default_return_fee: "0.00",
    suspend_count: "0",
    return_count: "0",
    is_late_fees: false,
    late_fees_through: null,
    is_payment_allocation: false,
    is_allow_pay_ahead_payment: false,
    order_payments_by: null,
    order_payments_by_list: null,
    is_enabled_ptp: false,
    max_ptp_day: "0",
    payment_instructions: []
  });

  // Accordion state for payment instructions
  const [expandedInstructions, setExpandedInstructions] = useState({});

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const inputStyle = useMemo(
    () => ({
      backgroundColor: "transparent",
      ...(isTheme2 ? { fontSize: "14px" } : {}),
    }),
    [isTheme2]
  );
  const dateInputStyle = isTheme2 ? { fontSize: "14px" } : { borderRadius: 0 };
  const checkboxStyle = isTheme2 ? { fontSize: "14px" } : { borderRadius: 0, fontSize: "14px" };
  const dollarInputStyle = isTheme2 ? { fontSize: "14px" } : undefined;

  const headerTextColor = useMemo(() => isLightTheme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)', [isLightTheme]);
  const cardChromeStyle = useMemo(() => ({
    boxShadow: 'none',
    border: isLightTheme ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.08)'
  }), [isLightTheme]);

  // Initialize formData when data changes
  useEffect(() => {
    if (data) {
      setFormData({
        default_return_fee: data.default_return_fee || "0.00",
        suspend_count: data.suspend_count || "0",
        return_count: data.return_count || "0",
        is_late_fees: data.is_late_fees || false,
        late_fees_through: data.late_fees_through,
        is_payment_allocation: data.is_payment_allocation || false,
        is_allow_pay_ahead_payment: data.is_allow_pay_ahead_payment || false,
        order_payments_by: data.order_payments_by,
        order_payments_by_list: data.order_payments_by_list,
        is_enabled_ptp: data.is_enabled_ptp || false,
        max_ptp_day: data.max_ptp_day || "0",
        payment_instructions: data.payment_instructions?.pages || []
      });
    }
  }, [data]);

  const orderPaymentsOptions = [
    { value: "balance_type", label: "Past Due Payments by Balance Type" },
    { value: "due_date", label: "Balance Type by Due Date" }
  ];

  const paymentTypes = [
    "Down Payments / Deferred Down Payments",
    "Regular Payments",
    "Miscellaneous Payments",
    "Side Note Payments",
    "Collateral Protection Insurance (CPI)",
    "Late Fee Payments"
  ];

  // Toggle accordion for payment instructions
  const toggleInstruction = (index) => {
    setExpandedInstructions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Expose handleSave to parent via ref
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      try {
        const payload = {
          eps: {
            default_return_fee: toNumber(formData.default_return_fee),
            suspend_count: toNumber(formData.suspend_count),
            return_count: toNumber(formData.return_count),
            is_late_fees: formData.is_late_fees,
            late_fees_through: formData.late_fees_through,
            is_payment_allocation: formData.is_payment_allocation,
            is_allow_pay_ahead_payment: formData.is_allow_pay_ahead_payment,
            order_payments_by: formData.order_payments_by,
            order_payments_by_list: formData.order_payments_by_list,
            is_enabled_ptp: formData.is_enabled_ptp,
            max_ptp_day: toNumber(formData.max_ptp_day),
            payment_instructions: {
              pages: formData.payment_instructions
            }
          }
        };

        await updateMutation.mutateAsync({
          id: portfolioId,
          data: payload
        });

        return { success: true };
      } catch (error) {
        console.error("Error saving EPS/PPD settings:", error);
        return { success: false, error };
      }
    }
  }));

  return (
    <div>
      {/* TRANSACTION DEFAULTS */}
      <Row>
        <Col md="4">
          <Card className="mb-4" style={cardChromeStyle}>
            <CardHeader className="font-weight-bold text-uppercase" style={{ color: headerTextColor }}>TRANSACTION DEFAULTS</CardHeader>


            <CardBody>
              <Row>
                <Col md="12">
                  <Col md="12">
                    <FormGroup>
                      <Label>Default Return Fee:</Label>
                      <DollarInput
                        className="form-control"
                        style={dollarInputStyle}
                        value={formData.default_return_fee}
                        onChange={(value) => setFormData(prev => ({ ...prev, default_return_fee: value }))}
                        placeholder="$0.00"
                      />
                    </FormGroup>
                  </Col>
                  <CardHeader className="font-weight-bold text-uppercase">Suspend / Return Settings</CardHeader>
                  <Col md="12">
                    <FormGroup>
                      <Label>Suspend Count:</Label>
                      <Input
                        type="number"
                        className="form-control"
                        style={inputStyle}
                        value={formData.suspend_count}
                        onChange={(e) => setFormData(prev => ({ ...prev, suspend_count: e.target.value }))}
                        min="0"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <Label>Return Count:</Label>
                      <Input
                        type="number"
                        className="form-control"
                        style={inputStyle}
                        value={formData.return_count}
                        onChange={(e) => setFormData(prev => ({ ...prev, return_count: e.target.value }))}
                        min="0"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <div className="form-check">
                        <Label className="form-check-label" htmlFor="waiveLateFees">
                          <Input
                            style={checkboxStyle}
                            type="checkbox"
                            id="waiveLateFees"
                            className="form-check-input"
                            checked={!!formData.is_late_fees}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_late_fees: e.target.checked }))}
                          />
                          <span className="form-check-sign" />
                          Waive Late Fees <i className="tim-icons icon-alert-circle-exc text-info ml-1" />
                        </Label>
                      </div>
                    </FormGroup>
                  </Col>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Waive late fees through:</Label>
                    <ReactDatetime
                      inputProps={{
                        className: "form-control",
                        placeholder: "Select Date",
                        style: dateInputStyle
                      }}
                      value={formData.late_fees_through ? formatDateForPicker(formData.late_fees_through) : ""}
                      onChange={(value) => setFormData(prev => ({ ...prev, late_fees_through: value }))}
                      dateFormat="MM-DD-YYYY"
                      timeFormat={false}
                      closeOnSelect={true}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="mb-4" style={cardChromeStyle}>
            <CardHeader className="font-weight-bold text-uppercase" style={{ color: headerTextColor }}>
              PAYMENT ALLOCATION
              <div className="custom-control custom-switch float-right">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="paymentAllocation"
                  checked={formData.is_payment_allocation}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_payment_allocation: e.target.checked }))}
                />
                <label className="custom-control-label" htmlFor="paymentAllocation"></label>
              </div>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>
                      Allow Pay Ahead 1 Payment:
                      <i className="tim-icons icon-alert-circle-exc text-info ml-1" />
                    </Label>
                    <div className="form-check">
                      <Label className="form-check-label" htmlFor="allowPayAhead">
                        <Input
                          style={checkboxStyle}
                          type="checkbox"
                          id="allowPayAhead"
                          className="form-check-input"
                          checked={!!formData.is_allow_pay_ahead_payment}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_allow_pay_ahead_payment: e.target.checked }))}
                        />
                        <span className="form-check-sign" />
                        Allow Pay Ahead 1 Payment
                      </Label>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>
                      Order Payments by:
                      <i className="tim-icons icon-alert-circle-exc text-info ml-1" />
                    </Label>
                    <div className="mt-2">
                      {orderPaymentsOptions.map((option, index) => (
                        <div key={index} className="custom-control custom-radio">
                          <input
                            type="radio"
                            className="custom-control-input"
                            id={`orderPayments${index}`}
                            name="orderPayments"
                            value={option.value}
                            checked={formData.order_payments_by === option.value}
                            onChange={(e) => setFormData(prev => ({ ...prev, order_payments_by: e.target.value }))}
                          />
                          <label className="custom-control-label" htmlFor={`orderPayments${index}`}>
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>Payment Types Order:</Label>
                    <div className="border p-3" style={{ backgroundColor: '#f8f9fa' }}>
                      {paymentTypes.map((type, index) => (
                        <div key={index} className="d-flex align-items-center mb-2">
                          <i className="tim-icons icon-drag-handle text-muted mr-2" style={{ cursor: 'move' }} />
                          <span>{type}</span>
                        </div>
                      ))}
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          {/* PTP (Promise To Pay) */}
          <Card className="mb-4" style={cardChromeStyle}>
            <CardHeader className="font-weight-bold text-uppercase" style={{ color: headerTextColor }}>PTP (Promise To Pay)</CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Enable Stacked PTPs:</Label>
                    <div className="form-check">
                      <Label className="form-check-label" htmlFor="enableStackedPTP">
                        <Input
                          style={checkboxStyle}
                          type="checkbox"
                          id="enableStackedPTP"
                          className="form-check-input"
                          checked={!!formData.is_enabled_ptp}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_enabled_ptp: e.target.checked }))}
                        />
                        <span className="form-check-sign" />
                        Enable Stacked PTPs
                      </Label>
                    </div>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Max PTP Days:</Label>
                    <Input
                      type="number"
                      className="form-control"
                      style={inputStyle}
                      value={formData.max_ptp_day}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_ptp_day: e.target.value }))}
                      min="0"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* EPS PAYMENT INSTRUCTIONS */}
      <Card className="mb-4" style={cardChromeStyle}>
        <CardHeader className="font-weight-bold text-uppercase" style={{ color: headerTextColor }}>EPS PAYMENT INSTRUCTIONS</CardHeader>
        <CardBody>
          <EpsInstructionsAccordion
            instructions={formData.payment_instructions}
            onChange={(next) => setFormData((prev) => ({ ...prev, payment_instructions: next }))}
            activeColorStyle={activeColorStyle}
            isLightTheme={isLightTheme}
            isTheme2={isTheme2}
            cardChromeStyle={cardChromeStyle}
            inputStyle={inputStyle}
          />
        </CardBody>
      </Card>
    </div>
  );
});

PortfolioEpsPpd.displayName = "PortfolioEpsPpd";

export default PortfolioEpsPpd;

